import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { CancelButton } from 'src/components/Buttons';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContextEventsTransformedSelector } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { Button, Stack } from 'src/core/ui';
import {
  useCreateAdyenPaymentSession,
  useSubmitAdyenPaymentResult,
} from 'src/hooks/api/useAdyenPaymentSession';
import { useEventItemLoadingDisplay } from 'src/hooks/useEventItemLoadingDisplay';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  ApiException,
  CreateRetailSaleWithPaymentInput,
  ListingDetails,
  SaleClient,
  TicketType,
} from 'src/WebApiController';

import { CancellableFormHeader } from '../common/CancellableFormHeader';
import { HeaderContent } from '../common/EventEntityHeader/EventEntityHeader.styled';
import { modalDetails } from '../common/Modals.css';
import { Summary } from '../common/Summary';
import {
  ModalBody,
  ModalBodyHeaderContainer,
  ModalFooter,
} from '../Modal/Modal.styled';
import { RetailSaleDetailActions } from './components/RetailSaleDetailView';
import * as styles from './RetailSale.css';
import { RetailSaleBody } from './RetailSaleBody';
import { RetailSaleFormValues } from './types';

export type RetailSaleStep = 'buyer' | 'checkout' | 'detail';

export const RetailSale = () => {
  const { posEntity, posEntityDisplayId, posEntityId } =
    useActivePosEntityContext<ListingDetails>();

  const listing = useMemo(() => {
    return posEntity?.entityType === ActionOutboxEntityType.Listing
      ? (posEntity as ListingDetails)
      : undefined;
  }, [posEntity]);

  const { isLoading: entityIsLoading, loadingState } =
    useEventItemLoadingDisplay<ListingDetails>(
      FormatContentId.LoadingListingId,
      FormatContentId.SearchingForListingId,
      FormatContentId.CouldNotFindListingId,
      posEntityDisplayId ?? posEntityId?.toString()
    );

  const methods = useForm<RetailSaleFormValues>({
    defaultValues: {},
  });

  useEffect(() => {
    if (listing) {
      methods.reset({
        externalSaleId: `Retail-${new Date().getTime()}`,
        listingId: listing.id ?? 0,
        currencyCode: listing.currency ?? 'USD',
        saleDate: new Date().toISOString(),
        useShippingAddressOnFile: false,
        billingAddressSameAsShipping: true,
        tags: listing.tags,
        quantitySold: listing.availQty,
        section: listing.seating.section,
        row: listing.seating.row,
        seatFrom: listing.seating.seatFr,
        seatTo: listing.seating.seatTo,
        lineItems: [],
      });
    }
  }, [listing, methods]);

  if (!listing && entityIsLoading) {
    return loadingState;
  }

  return (
    <FormProvider {...methods}>
      <RetailSaleContent listing={listing} />
    </FormProvider>
  );
};

const buildRetailSaleInput = (
  values: RetailSaleFormValues,
  listing: ListingDetails | undefined,
  merchantReference: string | null
): CreateRetailSaleWithPaymentInput => {
  return {
    listingId: values.listingId,
    posEventId: listing?.posEvId ?? '',
    viagogoEventId: listing?.viagEvId ?? null,
    eventMappingId: listing?.viagEvMapId ?? null,
    venueId: null,
    saleDate: values.saleDate ?? null,
    currencyCode: values.currencyCode ?? null,
    totalNetProceeds:
      values.lineItems?.reduce((sum, item) => sum + (item.amount ?? 0), 0) ??
      null,
    inHandDate: values.inHandDate ?? null,
    quantitySold: listing?.ticketCnt ?? null,
    section: listing?.seating?.section ?? null,
    row: listing?.seating?.row ?? null,
    seatFrom: listing?.seating?.seatFr ?? null,
    seatTo: listing?.seating?.seatTo ?? null,
    ticketType: (values.ticketType as TicketType) ?? null,
    buyerName: values.buyerName ?? null,
    buyerEmail: values.buyerEmail ?? null,
    buyerPhone: values.buyerPhone ?? null,
    internalNotes: values.privateNotes ?? null,
    merchantReference,
    lineItems: values.lineItems?.map((li) => ({
      description: li.description,
      amount: li.amount,
      ratePercent: li.ratePercent ?? null,
      reference: li.reference ?? null,
    })),
  };
};

const RetailSaleContent = ({ listing }: { listing?: ListingDetails }) => {
  const [step, setStep] = useState<RetailSaleStep>('buyer');
  const [completedSaleId, setCompletedSaleId] = useState<number | null>(null);
  const [detailActions, setDetailActions] =
    useState<RetailSaleDetailActions | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAdyenDropIn, setShowAdyenDropIn] = useState(false);
  const [adyenSession, setAdyenSession] = useState<{
    sessionId: string;
    sessionData: string;
    clientKey: string;
    environment: string;
    merchantReference: string;
  } | null>(null);

  const eventsTransformed = useCatalogDataContextEventsTransformedSelector();
  const event = useMemo(() => {
    return eventsTransformed?.find(
      (e) => e.event?.virtId === listing?.viagVirtId
    );
  }, [eventsTransformed, listing?.viagVirtId]);

  const continueToPaymentMsg = useContent(ContentId.ContinueToPayment);
  const completeAsUnpaidMsg = useContent(ContentId.CompleteAsUnpaid);
  const payAndCompleteMsg = useContent(ContentId.PayAndComplete);
  const cancelInvoiceMsg = useContent(ContentId.CancelInvoice);
  const refundMsg = useContent(ContentId.Refund);
  const cancelAndRefundMsg = useContent(ContentId.CancelInvoiceAndRefund);

  const { getValues } = useFormContext<RetailSaleFormValues>();
  const { closeModal } = useContext(ModalContext);
  const { showErrorDialog, genericError, trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();

  const createPaymentSession = useCreateAdyenPaymentSession();
  const submitPaymentResult = useSubmitAdyenPaymentResult();

  const goToCheckout = useCallback(() => setStep('checkout'), []);
  const goToBuyer = useCallback(() => setStep('buyer'), []);

  const onCancel = useCallback(() => {
    closeModal(true);
  }, [closeModal]);

  const onCompleteAsUnpaid = useCallback(() => {
    const values = getValues();
    setIsLoading(true);
    const saleInput = buildRetailSaleInput(values, listing, null);

    tryInvokeApi(
      async () => {
        const client = new SaleClient(activeAccountWebClientConfig);
        const result = await client.createRetailSaleWithPaymentForListingId(
          saleInput.listingId,
          saleInput
        );

        if (result.saleId) {
          setCompletedSaleId(result.saleId);
          setStep('detail');
        } else {
          showErrorDialog(
            'SaleClient.createRetailSaleWithPaymentForListingId',
            {
              message: result.message ?? genericError,
              status: 500,
            } as ApiException,
            { trackErrorData: saleInput }
          );
        }
      },
      (error) => {
        showErrorDialog(
          'SaleClient.createRetailSaleWithPaymentForListingId',
          error,
          { trackErrorData: saleInput }
        );
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [
    activeAccountWebClientConfig,
    genericError,
    getValues,
    listing,
    showErrorDialog,
  ]);

  const onPayAndComplete = useCallback(() => {
    const values = getValues();
    setIsLoading(true);

    tryInvokeApi(
      async () => {
        // Step 1: Create the Adyen payment session first (no saleId at this point)
        const merchantReference = `retail-${listing?.id ?? values.listingId}-${Date.now()}`;
        const sessionResponse = await createPaymentSession.mutateAsync({
          saleId: null,
          amount:
            values.lineItems?.reduce(
              (sum, item) => sum + (item.amount ?? 0),
              0
            ) ?? 0,
          currencyCode: values.currencyCode ?? 'USD',
          buyerEmail: values.buyerEmail ?? null,
          returnUrl: window.location.href,
          reference: merchantReference,
          countryCode: values.shippingCountry ?? null,
        });

        // Step 2: Store session and merchant reference, then show the Adyen Drop-in
        setAdyenSession({
          sessionId: sessionResponse.sessionId,
          sessionData: sessionResponse.sessionData,
          clientKey: sessionResponse.clientKey,
          environment: sessionResponse.environment,
          merchantReference,
        });
        setShowAdyenDropIn(true);
      },
      (error) => {
        showErrorDialog('RetailSale.onPayAndComplete', error, {
          trackErrorData: { listingId: listing?.id ?? values.listingId },
        });
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [createPaymentSession, getValues, listing, showErrorDialog]);

  const handleAdyenPaymentCompleted = useCallback(
    async (result: {
      resultCode: string;
      sessionResult?: string;
      billingAddress?: {
        street?: string;
        houseNumberOrName?: string;
        city?: string;
        stateOrProvince?: string;
        postalCode?: string;
        country?: string;
      };
      cardHolderName?: string;
    }) => {
      if (!adyenSession) return;

      const values = getValues();

      // Step 3a: Submit payment result to update payment status (saleId is unknown at this point)
      await submitPaymentResult.mutateAsync({
        saleId: undefined,
        input: {
          resultCode: result.resultCode,
          pspReference: null,
          sessionResult: result.sessionResult ?? null,
          merchantReference: adyenSession.merchantReference,
          billingAddress: result.billingAddress
            ? {
                street: result.billingAddress.street ?? null,
                houseNumberOrName:
                  result.billingAddress.houseNumberOrName ?? null,
                city: result.billingAddress.city ?? null,
                stateOrProvince: result.billingAddress.stateOrProvince ?? null,
                postalCode: result.billingAddress.postalCode ?? null,
                country: result.billingAddress.country ?? null,
              }
            : null,
          cardHolderName: result.cardHolderName ?? null,
        },
      });

      // Step 3b: Create the retail sale with the merchant reference from the payment session
      const saleInput = buildRetailSaleInput(
        values,
        listing,
        adyenSession.merchantReference
      );

      const client = new SaleClient(activeAccountWebClientConfig);
      const saleResult = await client.createRetailSaleWithPaymentForListingId(
        saleInput.listingId,
        saleInput
      );

      if (saleResult.saleId) {
        setCompletedSaleId(saleResult.saleId);
        setStep('detail');
      } else {
        showErrorDialog(
          'SaleClient.createRetailSaleWithPaymentForListingId',
          {
            message: saleResult.message ?? genericError,
            status: 500,
          } as ApiException,
          { trackErrorData: saleInput }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      adyenSession,
      genericError,
      getValues,
      listing,
      showErrorDialog,
      submitPaymentResult,
    ]
  );

  const handleAdyenError = useCallback(
    (error: { message: string }) => {
      trackError('AdyenDropIn.onError', new Error(error.message));
      setShowAdyenDropIn(false);
      setAdyenSession(null);
    },
    [trackError]
  );

  return (
    <>
      <CancellableFormHeader onClose={onCancel}>
        <HeaderContent>
          <Stack direction="row" gap="m" alignItems="center">
            <span
              onClick={step !== 'detail' ? goToBuyer : undefined}
              className={styles.breadcrumbHeader}
            >
              <Content id={ContentId.RetailSale} />
            </span>
            <span className={styles.breadcrumbSeparator}>&gt;</span>
            <span className={styles.breadcrumbItem}>
              <Content id={ContentId.Checkout} />
            </span>
          </Stack>
        </HeaderContent>
      </CancellableFormHeader>

      <ModalBody>
        {event?.event && (
          <ModalBodyHeaderContainer>
            <Summary
              event={event.event}
              posEntity={listing}
              entityType={ActionOutboxEntityType.Listing}
            />
          </ModalBodyHeaderContainer>
        )}
        <div className={modalDetails}>
          <RetailSaleBody
            listing={listing}
            step={step}
            saleId={completedSaleId}
            adyenSession={adyenSession}
            showAdyenDropIn={showAdyenDropIn}
            onAdyenPaymentCompleted={handleAdyenPaymentCompleted}
            onAdyenError={handleAdyenError}
            onAddNewCard={onPayAndComplete}
            onDetailActionsReady={setDetailActions}
          />
        </div>
      </ModalBody>

      <ModalFooter>
        {step === 'buyer' ? (
          <Stack direction="row" gap="m" justifyContent="end" width="full">
            <CancelButton type="button" onClick={onCancel} />
            <Button type="button" onClick={goToCheckout}>
              {continueToPaymentMsg}
            </Button>
          </Stack>
        ) : step === 'checkout' ? (
          <Stack direction="row" gap="m" justifyContent="end" width="full">
            <CancelButton type="button" onClick={onCancel} />
            <Button
              type="button"
              onClick={onCompleteAsUnpaid}
              disabled={isLoading}
              variant="outline"
            >
              {completeAsUnpaidMsg}
            </Button>
            <Button
              type="button"
              onClick={onPayAndComplete}
              disabled={isLoading}
            >
              {payAndCompleteMsg}
            </Button>
          </Stack>
        ) : step === 'detail' ? (
          <Stack direction="row" gap="m" justifyContent="end" width="full">
            <CancelButton type="button" onClick={onCancel} />
            <Button
              type="button"
              onClick={() => detailActions?.handleCancelInvoice()}
              disabled={detailActions?.isActioning}
              variant="outline"
            >
              {cancelInvoiceMsg}
            </Button>
            <Button
              type="button"
              onClick={() => detailActions?.handleRefund()}
              disabled={detailActions?.isActioning}
              variant="outline"
            >
              {refundMsg}
            </Button>
            <Button
              type="button"
              onClick={() => detailActions?.handleCancelAndRefund()}
              disabled={detailActions?.isActioning}
            >
              {cancelAndRefundMsg}
            </Button>
          </Stack>
        ) : null}
      </ModalFooter>
    </>
  );
};
